import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { parentApi, publicApi, PARENT_KEY, ACCESS_PAUSED_EVENT, storage } from "../lib/api";
import { isNativeBilling, initRevenueCat, purchasePlanNative, restoreNative } from "../billing/revenueCat";
import useAccessDeadline from "../hooks/useAccessDeadline";
import { stripeRedirectUrl } from "../lib/access";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [family, setFamily] = useState(null);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const isPaused = useAccessDeadline(family);

  const loadFamily = useCallback(async () => {
    const { data } = await parentApi.get("/family");
    setFamily(data.family);
    return data.family;
  }, []);

  const bootstrap = useCallback(async () => {
    const token = storage.get(PARENT_KEY);
    if (!token) { setUser(false); return; }
    try {
      const { data } = await parentApi.get("/auth/me");
      setUser(data.user);
      if (data.user.role !== "admin") await loadFamily();
    } catch (e) {
      storage.remove(PARENT_KEY);
      setUser(false);
      setFamily(null);
    }
  }, [loadFamily]);
  useEffect(() => { bootstrap(); }, [bootstrap]);

  useEffect(() => {
    const pause = (event) => {
      if (event.detail?.tokenKey !== PARENT_KEY) return;
      setFamily((current) => current ? { ...current, ...event.detail.access, has_access: false, can_edit: false } : current);
    };
    window.addEventListener(ACCESS_PAUSED_EVENT, pause);
    return () => window.removeEventListener(ACCESS_PAUSED_EVENT, pause);
  }, []);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    const refresh = () => { if (!document.hidden) loadFamily().catch(() => {}); };
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [user, loadFamily]);

  const login = async (email, password) => {
    const { data } = await publicApi.post("/auth/login", { email, password });
    storage.set(PARENT_KEY, data.token);
    setFamily(null);
    setUser(data.user);
    if (data.user.role !== "admin") await loadFamily();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await publicApi.post("/auth/register", payload);
    storage.set(PARENT_KEY, data.token);
    setFamily(null);
    setUser(data.user);
    await loadFamily();
    return data;
  };

  const logout = async () => {
    try { await parentApi.post("/auth/logout"); } catch (e) { /* local sign-out still succeeds offline */ }
    storage.remove(PARENT_KEY);
    setUser(false);
    setFamily(null);
  };

  const subscribe = async (plan) => {
    if (isNativeBilling()) {
      const state = await purchasePlanNative(family?.id, plan);
      if (state?.cancelled) return state;
      if (state?.isPlus) await parentApi.post("/billing/revenuecat/sync", { plan });
      const updated = await loadFamily();
      // Neither the SDK nor the checkout return URL can grant account access.
      return { family: updated, isPlus: updated?.has_access && updated?.access_reason === "subscription" };
    }
    const { data } = await parentApi.post("/billing/checkout", { plan });
    window.location.assign(stripeRedirectUrl(data.url));
    return { redirecting: true };
  };

  const restorePurchases = async () => {
    if (!isNativeBilling()) return null;
    const state = await restoreNative(family?.id);
    if (state?.isPlus) await parentApi.post("/billing/revenuecat/sync", { plan: state.plan });
    const updated = await loadFamily();
    return { family: updated, isPlus: updated?.has_access && updated?.access_reason === "subscription" };
  };

  const manageSubscription = async () => {
    const { data } = await parentApi.post("/billing/portal", {});
    window.location.assign(stripeRedirectUrl(data.url));
  };

  const syncStripe = useCallback(async () => {
    await parentApi.post("/billing/stripe/sync", {});
    return loadFamily();
  }, [loadFamily]);

  useEffect(() => {
    if (family?.id && isNativeBilling()) initRevenueCat(family.id).catch(() => {});
  }, [family?.id]);

  const isDemo = !!family?.is_demo;
  const canEdit = !!family?.can_edit && !isPaused;
  const demoGuard = () => {
    if (isDemo) { setNudgeOpen(true); return true; }
    return isPaused;
  };

  return (
    <AuthContext.Provider value={{ user, family, setFamily, login, register, logout, loadFamily, subscribe, manageSubscription, syncStripe, restorePurchases,
      isNativeBilling: isNativeBilling(), isPaused,
      isDemo, isOwner: !!family?.is_owner, canEdit,
      showEditControls: isDemo || canEdit, demoGuard,
      nudgeOpen, openNudge: () => setNudgeOpen(true), closeNudge: () => setNudgeOpen(false) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
